import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, ChevronDown, ChevronUp, Gift, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCustomerPoints } from '@/hooks/use-customer-points';
import { usePartnerOffers } from '@/hooks/use-partner-offers';

interface CondensedRewardsProps {
  expanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
  className?: string;
  organizationId?: string;
}

export function CondensedRewards({ expanded, onExpandChange, className, organizationId }: CondensedRewardsProps) {
  const [isActuallyExpanded, setIsActuallyExpanded] = useState(expanded);
  const { t } = useLanguage();
  const { points, loading: pointsLoading } = useCustomerPoints();
  const { offers, loading: offersLoading } = usePartnerOffers();

  const handleExpandToggle = () => {
    setIsActuallyExpanded(!isActuallyExpanded);
    onExpandChange?.(!isActuallyExpanded);
  };

  // Calculate tier based on total points
  const getTier = (totalPoints: number) => {
    if (totalPoints >= 10000) return t('rewards.diamondTier');
    if (totalPoints >= 8000) return t('rewards.platinumTier');
    if (totalPoints >= 6000) return t('rewards.goldTier');
    if (totalPoints >= 4000) return t('rewards.bronzeTier');
    return t('rewards.silverTier');
  };

  // Find the next tier threshold
  const getNextTierThreshold = (totalPoints: number) => {
    if (totalPoints < 2000) return 2000;
    if (totalPoints < 4000) return 4000;
    if (totalPoints < 6000) return 6000;
    if (totalPoints < 8000) return 8000;
    if (totalPoints < 10000) return 10000;
    return null;
  };

  // Calculate progress percentage
  const calculateProgress = (current: number, next: number | null) => {
    if (!next) return 100;
    return Math.min((current / next) * 100, 100);
  };

  const totalPoints = points?.totalPoints || 0;
  const currentTier = getTier(totalPoints);
  
  // Find the selected goal offer
  const selectedGoal = offers?.find(offer => offer.id === points?.selectedGoalId);
  const pointsNeeded = selectedGoal ? selectedGoal.pointsRequired - totalPoints : 0;
  
  // Calculate progress percentage for the selected goal
  const calculateGoalProgress = () => {
    if (!selectedGoal) return 0;
    return Math.min((totalPoints / selectedGoal.pointsRequired) * 100, 100);
  };

  const goalProgress = calculateGoalProgress();

  return (
    <motion.div 
      className={cn("relative", className)}
      layout
      transition={{ duration: 0.2 }}
    >
      <div className="bg-card border rounded-lg p-4">
        <motion.div layout className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <div className="space-y-1">
                {pointsLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm text-muted-foreground">{t('common.loading')}</span>
                  </div>
                ) : (
                  <>
                    <div className="text-2xl font-bold">{totalPoints} {t('rewards.points')}</div>
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "font-medium border-slate-400",
                        currentTier === t('rewards.diamondTier') && "text-blue-400 border-blue-400",
                        currentTier === t('rewards.platinumTier') && "text-purple-400 border-purple-400",
                        currentTier === t('rewards.goldTier') && "text-yellow-400 border-yellow-400",
                        currentTier === t('rewards.bronzeTier') && "text-orange-400 border-orange-400",
                        currentTier === t('rewards.silverTier') && "text-gray-400 border-gray-400"
                      )}
                    >
                      <Star className="h-3 w-3 mr-1" />
                      {currentTier}
                    </Badge>
                  </>
                )}
              </div>
              
              <div className="flex items-center gap-4">
                <div className="relative w-16 h-16">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                    <path
                      d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#eee"
                      strokeWidth="3"
                    />
                    <path
                      d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#7dda55"
                      strokeWidth="3"
                      strokeDasharray={`${goalProgress}, 100`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-medium">
                      {goalProgress.toFixed(1)}%
                    </span>
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="text-sm font-medium flex items-center gap-1">
                    <Gift className="h-4 w-4" />
                    {t('rewards.currentGoal')}
                  </div>
                  {offersLoading ? (
                    <div className="text-xs text-muted-foreground">
                      {t('common.loading')}
                    </div>
                  ) : selectedGoal ? (
                    <>
                      <div className="text-xs text-muted-foreground">
                        {t('rewards.needs')} {pointsNeeded} {t('rewards.points')}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {selectedGoal.title} - {selectedGoal.discountPercentage}%
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-muted-foreground">
                      {t('rewards.noGoalSet')}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExpandToggle}
            >
              {isActuallyExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}